import React from 'react';
import {
  RichTextEditor,
  ImageEmbedEditor,
  VideoEmbedEditor,
  PDFUploadEditor,
  CodeFileEditor,
  SCORMUploadEditor,
  VideoMeetingEditor,
  EvaluationEditor,
  FeedbackEditor,
} from './index';
import type { ModuleType } from '../../../types/course';

export interface ContentBlockModule {
  id: string;
  title: string;
  type: ModuleType | string;
  details: Record<string, unknown>;
}

interface ContentBlockRendererProps {
  module: ContentBlockModule;
  sectionId: string;
  courseId: string;
  onUpdate: (moduleId: string, updates: Record<string, unknown>) => Promise<void>;
  onDelete: (moduleId: string) => Promise<void>;
  /** When true, editors suppress their ModuleEditorBase chrome (used inside NotionBlockEditor) */
  embedded?: boolean;
  /** Fullscreen state — passed to editors that support it */
  isFullscreen?: boolean;
  onToggleFullscreen?: () => void;
  /** Register a cancel/rollback callback from the child editor */
  registerCancelCallback?: (cb: () => Promise<void>) => void;
  /** Bubbles live rich-text HTML so a host can render a live preview. */
  onContentChange?: (html: string) => void;
}

/**
 * ContentBlockRenderer — maps a module type to the correct editor component.
 *
 * After the aggregate-modules refactor, evaluation + feedback are containers
 * and the 14 old per-subtype types have been removed.
 */
export default function ContentBlockRenderer({
  module,
  sectionId,
  courseId,
  onUpdate,
  onDelete,
  embedded = false,
  isFullscreen,
  onToggleFullscreen,
  registerCancelCallback,
  onContentChange,
}: ContentBlockRendererProps) {
  const moduleObj = {
    id: module.id,
    title: module.title,
    type: module.type,
    details: module.details ?? {},
    embedded,
    registerCancelCallback,
    isFullscreen,
    onToggleFullscreen,
  };

  const commonProps = {
    module: moduleObj,
    sectionId,
    courseId,
    onUpdate,
    onDelete,
  };

  const editors: Record<string, React.ReactNode> = {
    rich_text: <RichTextEditor {...commonProps} onContentChange={onContentChange} />,
    image_embed: <ImageEmbedEditor {...commonProps} />,
    video_url_embed: <VideoEmbedEditor {...commonProps} />,
    pdf_file: <PDFUploadEditor {...commonProps} />,
    source_code_file: <CodeFileEditor {...commonProps} />,
    scorm_zip: <SCORMUploadEditor {...commonProps} />,
    video_meeting_link: <VideoMeetingEditor {...commonProps} />,
    evaluation: <EvaluationEditor {...commonProps} />,
    feedback: <FeedbackEditor {...commonProps} />,
  };

  return (
    <div key={module.id}>
      {editors[module.type] ?? (
        <p className="text-sm text-text-secondary text-center py-8">
          Editor not available for this content type.
        </p>
      )}
    </div>
  );
}
